import { useCallback, useRef } from 'react';
import axios from 'axios';
import useUrlParams from './useUrlParams';
import moment from 'moment/moment';
import { useSelector } from 'react-redux';
import { DEFAULT_AGG_SIZE } from '../components/blocks/filters/MePosSaleFilters/constants';
import { parseCategoryAggregations } from '../components/blocks/sales/parsers/parseCategoryAggregations';
import { useTranslation } from 'react-i18next';
import {
  convertAggregationBucketsToOptions, convertAggWarehousesBucketsToOptions
} from '../components/blocks/sales/utils/helpers';

moment.updateLocale('en', {
  week: {
    dow: 1
  }
});

axios.interceptors.request.use((config) => {
  const { token } = useUrlParams();
  if (token) {
    config.headers['X-AUTH-TOKEN'] = token;
  }
  config.withCredentials = true;
  return config;
});

const useSalesRequest = () => {
  const { t } = useTranslation();
  const { baseUrl } = useUrlParams();
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);

  const {
    periodStart, periodEnd, statisticType, employee, category, brand, productSkuBarcode, warehouse
  } = useSelector(state => state.saleFiltersSlice);

  const cancelPrevious = useCallback(() => {
    if (abortRef.current) {
      try {
        abortRef.current.abort();
      } catch (e) {}
      abortRef.current = null;
    }
  }, []);

  const getSalesCount = async ({ startDate, endDate, signal }) => {
    if (!startDate || !endDate) return 0;

    const body = {
      start_date: startDate, end_date: periodEnd, size: 0
    };

    try {
      const { data: { documents } } = await axios.post(baseUrl, body, { signal });
      return documents?.hits?.total?.value || 0;
    } catch (e) {
      throw e;
    } finally {}
  };

  const getOptions = async ({ startDate, endDate, extraQueryMust = [], signal } = {}) => {
    if (!startDate || !endDate) {
      return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
    }

    const cacheKey = `${startDate}__${endDate}__${JSON.stringify(extraQueryMust)}`;
    if (cacheRef.current.has(cacheKey)) {
      return cacheRef.current.get(cacheKey);
    }

    cancelPrevious();
    abortRef.current = new AbortController();
    const usedSignal = signal || abortRef.current.signal;

    const body = {
      start_date: startDate,
      end_date: periodEnd,
      size: 0,
      query: {
        bool: {
          must: extraQueryMust
        }
      },
      aggs: {
        employees: {
          terms: { field: 'employee.id', size: DEFAULT_AGG_SIZE },
          aggs: {
            surname: { terms: { field: 'employee.surname.keyword', size: 1 } },
            name: { terms: { field: 'employee.name.keyword', size: 1 } }
          }
        },

        categories: {
          terms: { field: 'product.productCategory.id', size: DEFAULT_AGG_SIZE },
          aggs: {
            category: {
              terms: {
                field: 'product.productCategory.name.keyword'
              }
            },
            parentCategoryId: {
              terms: {
                field: 'product.productCategory.parentCategory.id'
              }
            },
            parentCategoryName: {
              terms: {
                field: 'product.productCategory.parentCategory.name.keyword'
              }
            },
            parentCategoryDeleted: {
              terms: {
                field: 'product.productCategory.parentCategory.deleted'
              }
            },
            categoryDeleted: {
              terms: {
                field: 'product.productCategory.deleted'
              }
            },
            subcategories: {
              terms: { field: 'product.productCategory.name.keyword' },
              aggs: {
                subcategoryId: {
                  terms: {
                    field: 'product.productCategory.id'
                  }
                },
                subcategoryDeleted: {
                  terms: {
                    field: 'product.productCategory.deleted'
                  }
                }
              }
            }
          }
        },

        brands: {
          terms: { field: 'product.brand.keyword', size: DEFAULT_AGG_SIZE },
          aggs: {
            brandName: { terms: { field: 'product.brand.keyword', size: 1 } }
          }
        },

        warehouses: {
          terms: { field: 'storage.id', size: DEFAULT_AGG_SIZE },
          aggs: {
            warehouseName: { terms: { field: 'storage.name.keyword', size: 1 }},
            warehouseDeleted: { terms: { field: 'storage.deleted' }},
          }
        },
        product_names: { terms: { field: 'product.name.keyword', size: DEFAULT_AGG_SIZE } },
        skus: { terms: { field: 'product.sku.keyword', size: DEFAULT_AGG_SIZE } },
        barcodes: { terms: { field: 'product.barcode.keyword', size: DEFAULT_AGG_SIZE } }
      }
    };

    try {
      const response = await axios.post(baseUrl, body, { signal: usedSignal });
      const aggregations = response?.data?.aggregations || {};
      const selfPermission = response?.data?.selfPermission === true;
      const employeesOptions = convertAggregationBucketsToOptions(aggregations.employees?.buckets || [], {
        valueKey: 'key', labelFields: ['name', 'surname'], idToString: true
      });
      const brandsOptions = convertAggregationBucketsToOptions(aggregations.brands?.buckets || [], {
        valueKey: 'key', labelFields: ['brandName'], idToString: true
      });
      const warehousesOptions = convertAggWarehousesBucketsToOptions(aggregations.warehouses?.buckets || []);
      const product_names = aggregations.product_names.buckets.map(b => b.key);
      const skus = aggregations.skus.buckets.map(b => b.key);
      const barcodes = aggregations.barcodes.buckets.map(b => b.key);
      const mergedSuggestions = [...product_names, ...skus, ...barcodes];
      const result = {
        employees: employeesOptions,
        categories: parseCategoryAggregations(aggregations.categories?.buckets, t),
        brands: brandsOptions,
        warehouses: warehousesOptions,
        mergedSuggestions,
        selfPermission
      };

      cacheRef.current.set(cacheKey, result);
      return result;
    } catch (e) {
      if (e.name === 'AbortError' || e.message === 'canceled') {
        return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
      }
      console.error('getOptions error', e);
      return { employees: [], categories: [], brands: [], warehouses: [], selfPermission: false };
    }
  };

  const getSalesData = async () => {
    cancelPrevious();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    const must = [];

    if (employee) {
      must.push({ term: { 'employee.id': Number(employee?.key) } });
    }
    if (category) {
      must.push({ term: { 'product.productCategory.id': Number(category) } });
    }
    if (brand) {
      must.push({ term: { 'product.brand.keyword': brand } });
    }
    if (productSkuBarcode) {
      const trimProductSkuBarcode = productSkuBarcode.trim();
      must.push({
        bool: {
          should: [{
            match_phrase: { 'product.sku': trimProductSkuBarcode } },
            { match_phrase: { 'product.barcode': trimProductSkuBarcode } },
            { match_phrase: { 'product.name.keyword': trimProductSkuBarcode } },
            { match_phrase: { 'product.name': trimProductSkuBarcode } }],
          minimum_should_match: 1
        }
      });
    }
    if (warehouse) {
      must.push({ term: { 'storage.id': Number(warehouse?.key) } });
    }

    let body = null;

    if (statisticType === 'DAILY_SALES') {
      body = {
        start_date: periodStart,
        end_date: periodEnd,
        size: 0,
        query: {
          bool: {
            must
          }
        },
        runtime_mappings: {
          cost_total: {
            type: 'double', script: {
              source: 'emit((doc[\'cost\'].size()!=0 ? doc[\'cost\'].value : 0) * (doc[\'quantity\'].size()!=0 ? doc[\'quantity\'].value : 0));'
            }
          }
        },
        aggs: {
          by_day: {
            date_histogram: {
              field: 'date',
              calendar_interval: 'day',
              order: { _key: 'asc' },
              min_doc_count: 0,
              format: 'yyyy-MM-dd',
              extended_bounds: {
                min: periodStart,
                max: periodEnd
              }
            },
            aggs: {
              date: { terms: { field: 'date', size: 1 } },
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
            }
          },

          total_sales_count: { value_count: { field: 'id' } },
          total_amount: { sum: { field: 'amount' } },
          total_cost: { sum: { field: 'cost_total' } },
          total_profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
        }
      };
    } else if (statisticType === 'POPULAR_PRODUCTS') {
      body = {
        start_date: periodStart,
        end_date: periodEnd,
        size: 0,
        query: {
          bool: {
            must
          }
        }, runtime_mappings: {
          cost_total: {
            type: 'double', script: {
              source: 'emit((doc[\'cost\'].size()!=0 ? doc[\'cost\'].value : 0) * (doc[\'quantity\'].size()!=0 ? doc[\'quantity\'].value : 0));'
            }
          }
        },
        aggs: {
          popular_items_by_count: {
            terms: {
              field: 'product.id',
              size: 20,
              order: { 'sales_count': 'desc' }
            },
            aggs: {
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } },
              product_name: { terms: { field: 'product.name.keyword', size: 1 } },
              brand: { terms: { field: 'product.brand.keyword', size: 1 } },
              category: { terms: { field: 'product.productCategory.name.keyword', size: 1 } },
              parentCategory: { terms: { field: 'product.productCategory.parentCategory.name.keyword', size: 1 } }
            }
          },
          popular_items_by_profit: {
            terms: {
              field: 'product.id', size: 20,
              order: { 'profit': 'desc' }
            },
            aggs: {
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } },
              product_name: { terms: { field: 'product.name.keyword', size: 1 } },
              brand: { terms: { field: 'product.brand.keyword', size: 1 } },
              category: { terms: { field: 'product.productCategory.name.keyword', size: 1 } }
            }
          },
          popular_categories_by_count: {
            terms: {
              field: 'product.productCategory.id',
              size: 20,
              order: { 'sales_count': 'desc' }
            },
            aggs: {
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } },
              category_name: { 'terms': { 'field': 'product.productCategory.name.keyword', 'size': 1 } }
            }
          },
          popular_categories_by_profit: {
            terms: {
              field: 'product.productCategory.id',
              size: 20,
              order: { 'profit': 'desc' }
            },
            aggs: {
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } },
              category_name: { 'terms': { 'field': 'product.productCategory.name.keyword', 'size': 1 } }
            }
          },

          total_sales_count: { value_count: { field: 'id' } },
          total_amount: { sum: { field: 'amount' } },
          total_cost: { sum: { field: 'cost_total' } },
          total_profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
        }
      };
    } else if (statisticType === 'EMPLOYEES_SALES') {
      body = {
        start_date: periodStart,
        end_date: periodEnd,
        size: 0,
        query: {
          bool: {
            must
          }
        },
        runtime_mappings: {
          cost_total: {
            type: 'double',
            script: {
              source: `
                double cost = doc['cost'].size() > 0 ? doc['cost'].value : 0.0;
                double quantity = doc['quantity'].size() > 0 ? doc['quantity'].value : 0.0;
                emit(cost * quantity);
              `
            }
          }
        },
        aggs: {
          employees_by_amount: {
            terms: {
              field: 'employee.id',
              size: 20,
              order: { amount_sum: 'desc' }
            },
            aggs: {
              surname: { terms: { field: 'employee.surname.keyword', size: 1 } },
              name: { terms: { field: 'employee.name.keyword', size: 1 } },
              employee_image: { terms: { field: 'employee.image.keyword', size: 1 } },
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
            }
          },

          employees_by_count: {
            terms: {
              field: 'employee.id',
              size: 20,
              order: { sales_count: 'desc' }
            },
            aggs: {
              surname: { terms: { field: 'employee.surname.keyword', size: 1 } },
              name: { terms: { field: 'employee.name.keyword', size: 1 } },
              employee_image: { terms: { field: 'employee.image.keyword', size: 1 } },
              sales_count: { value_count: { field: 'id' } },
              amount_sum: { sum: { field: 'amount' } },
              cost_sum: { sum: { field: 'cost_total' } },
              profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
            }
          },

          total_sales_count: { value_count: { field: 'id' } },
          total_amount: { sum: { field: 'amount' } },
          total_cost: { sum: { field: 'cost_total' } },
          total_profit: { sum: { script: { source: "doc['amount'].value - doc['cost_total'].value" } } }
        }
        };
      }
    else
      {
        body = {
          start_date: periodStart, end_date: periodEnd, size: 0, query: { bool: { must } }
        };
      }
      try {
        const response = await axios.post(baseUrl, body, { signal });
        return response?.data || null;
      } catch (e) {
        console.error('ES getSalesData error:', e);
        if (e && (e.name === 'AbortError' || e.message === 'canceled' || e.name === 'CanceledError')) {
          return null;
        }
        return null;
      }
    }

    return { getSalesCount, getOptions, getSalesData };
  };

  export default useSalesRequest;
